import { ReactNode, HTMLAttributes } from 'react';
import clsx from 'clsx';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: 'default' | 'glass' | 'glow' | 'gradient';
  hoverable?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export function Card({
  children,
  variant = 'default',
  hoverable = true,
  padding = 'md',
  className,
  ...props
}: CardProps) {
  return (
    <div
      className={clsx(
        'rounded-xl transition-all duration-300',

        // Variant styles
        {
          // Default: Solid dark card
          'bg-slate-800 border border-slate-700': variant === 'default',

          // Glass: Glassmorphism effect
          'glass-card': variant === 'glass',

          // Glow: Card with orange glow
          'bg-slate-800 border border-orange-500/30':
            variant === 'glow',
          'hover-glow': variant === 'glow',

          // Gradient: Gradient border
          'gradient-border': variant === 'gradient',
        },

        // Hover effects
        {
          'hover:-translate-y-1 hover:shadow-xl cursor-pointer': hoverable,
        },

        // Padding
        {
          'p-0': padding === 'none',
          'p-4': padding === 'sm',
          'p-6': padding === 'md',
          'p-8': padding === 'lg',
        },

        className
      )}
      {...props}
    >
      {variant === 'gradient' && (
        <div className="relative z-10 bg-slate-800 rounded-xl p-6">
          {children}
        </div>
      )}
      {variant !== 'gradient' && children}
    </div>
  );
}

interface CardHeaderProps {
  children: ReactNode;
  className?: string;
}

export function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div className={clsx('border-b border-slate-700 pb-4 mb-4', className)}>
      {children}
    </div>
  );
}

interface CardTitleProps {
  children: ReactNode;
  className?: string;
}

export function CardTitle({ children, className }: CardTitleProps) {
  return (
    <h3
      className={clsx(
        'text-xl font-semibold text-white',
        className
      )}
    >
      {children}
    </h3>
  );
}

interface CardContentProps {
  children: ReactNode;
  className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
  return <div className={className}>{children}</div>;
}

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div
      className={clsx('border-t border-slate-700 pt-4 mt-4', className)}
    >
      {children}
    </div>
  );
}
